/**
 * Enterprise QR Code Component
 * ─────────────────────────────
 * Renders a QR code that links to the public traceability page.
 * Uses qrcode.react for rendering.
 */
import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

interface QRCodeProps {
  code: string;
  size?: number;
  label?: string;
  downloadable?: boolean;
}

const QRCode: React.FC<QRCodeProps> = ({ code, size = 160, label, downloadable = true }) => {
  const traceUrl = `${window.location.origin}/t/${code}`;

  const handleDownload = () => {
    const svg = document.getElementById(`qr-${code}`) as unknown as SVGElement | null;
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = size + 80;
      canvas.height = size + 80;
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 40, 40, size, size);
        ctx.fillStyle = '#0F0E0D';
        ctx.font = '600 14px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(code, canvas.width / 2, size + 60);
        if (label) {
          ctx.font = '400 11px Inter, sans-serif';
          ctx.fillStyle = '#78716C';
          ctx.fillText(label, canvas.width / 2, size + 75);
        }
      }
      const link = document.createElement('a');
      link.download = `qr-${code}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="p-3 bg-white rounded-xl shadow-e2">
        <QRCodeSVG
          id={`qr-${code}`}
          value={traceUrl}
          size={size}
          level="H"
          includeMargin={false}
          fgColor="#0F0E0D"
          bgColor="#FFFFFF"
        />
      </div>
      {label && <p className="text-xs font-medium text-ink-600 dark:text-ink-300 text-center">{label}</p>}
      <p className="text-2xs font-mono text-ink-400">{code}</p>
      {downloadable && (
        <button onClick={handleDownload} className="btn-ghost text-2xs">
          <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
          Download PNG
        </button>
      )}
    </div>
  );
};

export default QRCode;
